import { Eye, Link03, SearchLg, Tag01 } from "@untitledui/icons";
import type { FC, ReactNode } from "react";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import type { ControllerRenderProps, FieldValues, Path } from "react-hook-form";
import { useCampaigns } from "@/api/campaigns";
import { useOpenCollaborationsList, useTargetCollaborationsList } from "@/api/collaborations";
import { useCreators } from "@/api/creator";
import { useCreatorCohorts } from "@/api/creator-cohorts";
import { useCreatorLists } from "@/api/creator-lists";
import { useCreatorTags } from "@/api/creator-tags";
import { useMerchantRisksList } from "@/api/merchant-risks";
import { useProducts } from "@/api/products";
import { useTikTokAdsAuthUrl, useTikTokAdsConnectionStatus } from "@/api/tiktok-ads";
import { getVideoStatusLabel, VIDEO_STATUS_OPTIONS } from "@/components/assurance/badges/video-status-badge";
import { BadgeWithButton } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Label } from "@/components/base/input/label";
import { CREATOR_UNAVAILABLE_REASONS, FilterSection, type UnavailableReason } from "@/components/filters/shared";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { buildMerchantRiskFilterGroups, type RiskFilterItem } from "@/components/safety-config/merchant-risks-select";
import { filterRiskOptionGroups, RiskOptionList } from "@/components/safety-config/risk-option-list";
import { Command } from "@/components/ui/command";
import { FormField } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useDebounce } from "@/hooks/use-debounce";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { getLogActionLabel } from "@/lib/assurance/action-types";
import { useFilters } from "@/providers/filter-provider";
import { getCreatorName } from "@/utils/creator-name";
import type { MultiSelectValue } from "./searchable-multi-select";
import { SearchableMultiSelect } from "./searchable-multi-select";

interface FilterFieldProps<TFieldValues extends FieldValues> {
  field: ControllerRenderProps<TFieldValues, Path<TFieldValues>>;
}

// Memoized ProductFilter component to prevent unnecessary re-renders
const ProductFilterInner = <TFieldValues extends FieldValues>({ field }: FilterFieldProps<TFieldValues>) => {
  const [productSearch, setProductSearch] = useState("");
  const debouncedSearch = useDebounce(productSearch);
  const { data: products, isLoading: isLoadingProducts } = useProducts(debouncedSearch);

  const options = useMemo(() => {
    if (!products) return undefined;
    const seen = new Set<string>();
    return products
      .filter((product) => {
        if (seen.has(product.productName)) return false;
        seen.add(product.productName);
        return true;
      })
      .map((product) => ({
        id: product.productId,
        label: product.productName,
      }));
  }, [products]);

  const handleSetSearch = useCallback((search: string) => setProductSearch(search), []);

  return (
    <SearchableMultiSelect
      field={field}
      label="Product"
      search={productSearch}
      setSearch={handleSetSearch}
      options={options}
      isLoading={isLoadingProducts}
      placeholder="Search..."
      emptyMessage="No products found"
      loadingMessage="Loading..."
    />
  );
};

export const ProductFilter = memo(ProductFilterInner) as typeof ProductFilterInner;

// Memoized CreatorFilter component to prevent unnecessary re-renders
const CreatorFilterInner = <TFieldValues extends FieldValues>({ field }: FilterFieldProps<TFieldValues>) => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const { data: creators, isLoading } = useCreators(debouncedSearch);

  const options = useMemo(
    () =>
      creators?.map((creator) => {
        const { nickname, handle } = getCreatorName(creator.nickname, creator.creatorName);
        return { id: creator.creatorId, label: nickname ?? handle ?? creator.creatorId };
      }),
    [creators],
  );

  const handleSetSearch = useCallback((value: string) => setSearch(value), []);

  return (
    <SearchableMultiSelect
      field={field}
      label="Creator"
      search={search}
      setSearch={handleSetSearch}
      options={options}
      isLoading={isLoading}
      placeholder="Search..."
      emptyMessage="No creators found"
      loadingMessage="Loading..."
    />
  );
};

export const CreatorFilter = memo(CreatorFilterInner) as typeof CreatorFilterInner;

const UNBOX_LISTS = [
  { id: "unbox:recommended_creators", label: "Best Matches" },
  { id: "unbox:top_creator", label: "Top Performers" },
  { id: "unbox:active_partner", label: "Active Partners" },
  { id: "unbox:dormant_creator", label: "Dormant" },
];

// Memoized CreatorListFilter component to prevent unnecessary re-renders
const CreatorListFilterInner = <TFieldValues extends FieldValues>({
  field,
  availableClassifications,
}: FilterFieldProps<TFieldValues> & { availableClassifications?: string[] }) => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const { data: creatorLists, isLoading } = useCreatorLists({ offset: 0, limit: 20 }, debouncedSearch, {
    column: "name",
    direction: "desc",
  });

  const unboxOptions = useMemo(() => {
    const matching = search
      ? UNBOX_LISTS.filter((l) => l.label.toLowerCase().includes(search.toLowerCase()))
      : UNBOX_LISTS;
    if (!availableClassifications) return matching;
    const available = new Set(availableClassifications);
    return matching.map((list) => {
      const classification = list.id.replace("unbox:", "");
      return classification === "recommended_creators" || available.has(classification)
        ? list
        : { ...list, unavailableReason: CREATOR_UNAVAILABLE_REASONS.list };
    });
  }, [search, availableClassifications]);

  const customOptions = useMemo(
    () =>
      creatorLists?.lists.map((list) => ({
        id: list.id,
        label: list.name,
        unavailableReason: list.creatorCount === 0 ? CREATOR_UNAVAILABLE_REASONS.list : undefined,
      })) ?? [],
    [creatorLists?.lists],
  );

  const groups = useMemo(
    () => [
      { heading: "Unbox Lists", options: unboxOptions },
      { heading: "Your Lists", options: customOptions },
    ],
    [unboxOptions, customOptions],
  );

  const handleSetSearch = useCallback((value: string) => setSearch(value), []);

  return (
    <SearchableMultiSelect
      field={field}
      label="Creator List"
      search={search}
      setSearch={handleSetSearch}
      options={undefined}
      groups={groups}
      isLoading={isLoading && customOptions.length === 0}
      placeholder="Search..."
      emptyMessage="No creator lists found"
      loadingMessage="Loading..."
    />
  );
};

export const CreatorListFilter = memo(CreatorListFilterInner) as typeof CreatorListFilterInner;

// TagFilter — multi-select sourced from the brand's full tag library.
// Used for both the Include and Exclude tag pickers (label differentiates them).
export const TagFilter = <TFieldValues extends FieldValues>({
  field,
  label,
  disableUnassigned = false,
}: FilterFieldProps<TFieldValues> & { label: string; disableUnassigned?: boolean }) => {
  const [search, setSearch] = useState("");
  const { data: tags, isLoading } = useCreatorTags();

  const options = useMemo(() => {
    const all =
      tags?.map((tag) => ({
        id: tag.id,
        label: tag.name,
        unavailableReason: disableUnassigned && tag.assignedCount === 0 ? CREATOR_UNAVAILABLE_REASONS.tag : undefined,
      })) ?? [];
    return search ? all.filter((opt) => opt.label.toLowerCase().includes(search.toLowerCase())) : all;
  }, [tags, search, disableUnassigned]);

  return (
    <SearchableMultiSelect
      field={field}
      label={label}
      search={search}
      setSearch={setSearch}
      options={options}
      isLoading={isLoading}
      placeholder="Search tags..."
      emptyMessage="No tags found"
      loadingMessage="Loading..."
    />
  );
};

export const TagsFilterSection = () => {
  const { form, isActive } = useFilters();
  const creatorTagsEnabled = useFeatureFlag("F-creator-tags");

  if (!creatorTagsEnabled || !isActive("includeTags")) return null;

  return (
    <FilterSection id="tags" icon={<Tag01 className="w-5 h-5" />} title="Tags">
      <div data-testid="filter-tag-include">
        <FormField
          control={form.control}
          name="includeTags"
          render={({ field }) => <TagFilter field={field} label="Include tags" disableUnassigned />}
        />
      </div>
      <div data-testid="filter-tag-exclude">
        <FormField
          control={form.control}
          name="excludeTags"
          render={({ field }) => <TagFilter field={field} label="Exclude tags" />}
        />
      </div>
    </FilterSection>
  );
};

// CohortFilter — multi-select sourced from the brand's full cohort library.
// Used for both the Include and Exclude cohort pickers (label differentiates them).
export const CohortFilter = <TFieldValues extends FieldValues>({
  field,
  label,
  disableUnassigned = false,
}: FilterFieldProps<TFieldValues> & { label: string; disableUnassigned?: boolean }) => {
  const [search, setSearch] = useState("");
  const { data: cohorts, isLoading } = useCreatorCohorts();

  const options = useMemo(() => {
    const all =
      cohorts?.map((cohort) => ({
        id: cohort.id,
        label: cohort.name,
        unavailableReason:
          disableUnassigned && cohort.assignedCount === 0 ? CREATOR_UNAVAILABLE_REASONS.cohort : undefined,
      })) ?? [];
    return search ? all.filter((opt) => opt.label.toLowerCase().includes(search.toLowerCase())) : all;
  }, [cohorts, search, disableUnassigned]);

  return (
    <SearchableMultiSelect
      field={field}
      label={label}
      search={search}
      setSearch={setSearch}
      options={options}
      isLoading={isLoading}
      placeholder="Search cohorts..."
      emptyMessage="No cohorts found"
      loadingMessage="Loading..."
    />
  );
};

// Memoized ContentFeatureFilter component to prevent unnecessary re-renders
const ContentFeatureFilterInner = <TFieldValues extends FieldValues>({
  field,
  options,
  title,
}: FilterFieldProps<TFieldValues> & { options: { id: string; label: string }[]; title: string }) => {
  const [search, setSearch] = useState("");

  const filtered = useMemo(
    () => options.filter((opt) => opt.label.toLowerCase().includes(search.toLowerCase())),
    [options, search],
  );

  const handleSetSearch = useCallback((value: string) => setSearch(value), []);

  return (
    <SearchableMultiSelect
      field={field}
      label={title}
      search={search}
      setSearch={handleSetSearch}
      options={filtered}
      isLoading={false}
    />
  );
};

export const ContentFeatureFilter = memo(ContentFeatureFilterInner) as typeof ContentFeatureFilterInner;

// Memoized selected-risk badge — shown below the risk select, mirrors
// SelectedItemBadge in searchable-multi-select.tsx.
const RiskSelectedBadge = memo(
  ({ item, onRemove }: { item: RiskFilterItem; onRemove: (item: RiskFilterItem) => void }) => {
    const handleRemove = useCallback(() => onRemove(item), [onRemove, item]);

    return (
      <BadgeWithButton
        type="pill-color"
        size="md"
        color="black"
        buttonLabel={`Remove ${item.name} filter`}
        onButtonClick={handleRemove}
      >
        <span className="text-sm max-w-32 truncate">{item.name}</span>
      </BadgeWithButton>
    );
  },
);
RiskSelectedBadge.displayName = "RiskSelectedBadge";

// Memoized RiskTypeFilter component to prevent unnecessary re-renders.
//
// Options come from the merchant's currently-enabled risks (UNB-2388), not a
// static or historical list, grouped exactly like Configure Safety/"add risk"
// (buildMerchantRiskFilterGroups reuses buildRiskGroupsFromCatalog).
const RiskTypeFilterInner = <TFieldValues extends FieldValues>({
  field,
  getUnavailableReason,
}: FilterFieldProps<TFieldValues> & { getUnavailableReason?: UnavailableReason }) => {
  const { data, isLoading } = useMerchantRisksList();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const stringValues = (field.value as string[]) ?? [];

  const groups = useMemo(() => buildMerchantRiskFilterGroups(data ?? []), [data]);

  const filteredGroups = useMemo(() => filterRiskOptionGroups(groups, search), [groups, search]);

  const isSelected = useCallback(
    (item: RiskFilterItem) => item.riskIds.length > 0 && item.riskIds.every((v) => stringValues.includes(v)),
    [stringValues],
  );

  const handleToggle = useCallback(
    (item: RiskFilterItem) => {
      field.onChange(
        isSelected(item)
          ? stringValues.filter((v) => !item.riskIds.includes(v))
          : [...new Set([...stringValues, ...item.riskIds])],
      );
    },
    [field, stringValues, isSelected],
  );

  const handleClearSearch = useCallback(() => setSearch(""), []);

  const itemUnavailableReason = useCallback(
    (item: RiskFilterItem) => {
      if (!getUnavailableReason) return undefined;
      const reasons = item.riskIds.map(getUnavailableReason);
      return reasons.every(Boolean) ? reasons[0] : undefined;
    },
    [getUnavailableReason],
  );

  // A value can be in field.value with no matching item — the risk was disabled since,
  // or the value came from a stale URL. Render it as a fallback badge (using the raw
  // riskId as its label) instead of dropping it silently: it stays visible, stays
  // in effect, and stays individually removable via the same handleToggle path.
  const resolvedRiskIds = useMemo(
    () => new Set(groups.flatMap((g) => g.items).flatMap((item) => item.riskIds)),
    [groups],
  );

  const selectedItems = useMemo(() => {
    const fromGroups = groups.flatMap((g) => g.items).filter(isSelected);
    const fallbackItems: RiskFilterItem[] = stringValues
      .filter((v) => !resolvedRiskIds.has(v))
      .map((v) => ({ key: v, name: v, category: "", riskIds: [v] }));
    return [...fromGroups, ...fallbackItems];
  }, [groups, isSelected, stringValues, resolvedRiskIds]);

  return (
    <div className="flex flex-col gap-2">
      <Label className="text-sm">Risks in Video</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger className="w-full">
          <Input
            placeholder="Search Risk type"
            inputClassName="text-sm"
            icon={SearchLg}
            value={search}
            onChange={setSearch}
            onClear={search ? handleClearSearch : undefined}
          />
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-[var(--radix-popover-trigger-width)]"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command shouldFilter={false}>
            <RiskOptionList
              groups={filteredGroups}
              isLoading={isLoading}
              isSelected={isSelected}
              getUnavailableReason={itemUnavailableReason}
              onSelect={handleToggle}
            />
          </Command>
        </PopoverContent>
      </Popover>

      <div className="flex flex-row flex-wrap gap-1">
        {selectedItems.map((item) => (
          <RiskSelectedBadge key={item.key} item={item} onRemove={handleToggle} />
        ))}
      </div>
    </div>
  );
};

export const RiskTypeFilter = memo(RiskTypeFilterInner) as typeof RiskTypeFilterInner;

// Memoized OutcomeFilter component to prevent unnecessary re-renders
const OutcomeFilterInner = <TFieldValues extends FieldValues>({
  field,
  options,
}: FilterFieldProps<TFieldValues> & { options: string[] }) => {
  const [search, setSearch] = useState("");

  const stringValues = (field.value as string[]) ?? [];

  const uiValue: MultiSelectValue = useMemo(
    () => stringValues.map((v) => ({ id: v, label: getLogActionLabel(v) })),
    [stringValues],
  );

  const uiOptions = useMemo(
    () =>
      options
        .map((o) => ({ id: o, label: getLogActionLabel(o) }))
        .filter((opt) => opt.label.toLowerCase().includes(search.toLowerCase())),
    [options, search],
  );

  const handleSetSearch = useCallback((value: string) => setSearch(value), []);

  const handleChange = useCallback((val: MultiSelectValue) => field.onChange(val.map((v) => v.id)), [field]);

  const adaptedField = useMemo(
    () => ({
      ...field,
      value: uiValue as unknown as ControllerRenderProps<TFieldValues, Path<TFieldValues>>["value"],
      onChange: handleChange,
    }),
    [field, uiValue, handleChange],
  );

  return (
    <SearchableMultiSelect
      field={adaptedField}
      label="Outcome"
      search={search}
      setSearch={handleSetSearch}
      options={uiOptions}
      isLoading={false}
      placeholder="Search outcomes..."
      emptyMessage="No outcomes found"
      loadingMessage="Loading..."
    />
  );
};

export const OutcomeFilter = memo(OutcomeFilterInner) as typeof OutcomeFilterInner;

const SafetyStatusFilterInner = <TFieldValues extends FieldValues>({
  field,
  getUnavailableReason,
}: FilterFieldProps<TFieldValues> & { getUnavailableReason?: UnavailableReason }) => {
  const [search, setSearch] = useState("");

  const stringValues = (field.value as string[]) ?? [];

  const uiValue: MultiSelectValue = useMemo(
    () => stringValues.map((v) => ({ id: v, label: getVideoStatusLabel(v) })),
    [stringValues],
  );

  const uiOptions = useMemo(
    () =>
      VIDEO_STATUS_OPTIONS.filter((option) => option.label.toLowerCase().includes(search.toLowerCase())).map(
        (option) => ({ ...option, unavailableReason: getUnavailableReason?.(option.id) }),
      ),
    [search, getUnavailableReason],
  );

  const handleSetSearch = useCallback((value: string) => setSearch(value), []);

  const handleChange = useCallback((val: MultiSelectValue) => field.onChange(val.map((v) => v.id)), [field]);

  const adaptedField = useMemo(
    () => ({
      ...field,
      value: uiValue as unknown as ControllerRenderProps<TFieldValues, Path<TFieldValues>>["value"],
      onChange: handleChange,
    }),
    [field, uiValue, handleChange],
  );

  return (
    <SearchableMultiSelect
      field={adaptedField}
      label="Safety Status"
      search={search}
      setSearch={handleSetSearch}
      options={uiOptions}
      placeholder="Search safety statuses..."
      emptyMessage="No safety statuses found"
    />
  );
};

export const SafetyStatusFilter = memo(SafetyStatusFilterInner) as typeof SafetyStatusFilterInner;

// Memoized CategoryFilter component for product categories
// TODO: Replace static options with API call when category endpoint is available
const PRODUCT_CATEGORIES = [
  { id: "beauty", label: "Beauty & Personal Care" },
  { id: "skincare", label: "Skincare" },
  { id: "makeup", label: "Makeup" },
  { id: "haircare", label: "Haircare" },
  { id: "fragrance", label: "Fragrance" },
  { id: "wellness", label: "Wellness" },
];

const CategoryFilterInner = <TFieldValues extends FieldValues>({ field }: FilterFieldProps<TFieldValues>) => {
  const [search, setSearch] = useState("");

  const filteredOptions = useMemo(
    () => PRODUCT_CATEGORIES.filter((opt) => opt.label.toLowerCase().includes(search.toLowerCase())),
    [search],
  );

  const handleSetSearch = useCallback((value: string) => setSearch(value), []);

  return (
    <SearchableMultiSelect
      field={field}
      label="Category"
      search={search}
      setSearch={handleSetSearch}
      options={filteredOptions}
      isLoading={false}
      placeholder="Search categories..."
      emptyMessage="No categories found"
      loadingMessage="Loading..."
    />
  );
};

export const CategoryFilter = memo(CategoryFilterInner) as typeof CategoryFilterInner;

const CollaborationFilterInner = <TFieldValues extends FieldValues>({ field }: FilterFieldProps<TFieldValues>) => {
  const [search, setSearch] = useState("");

  const { data: openCollabs, isLoading: isLoadingOpen } = useOpenCollaborationsList({
    search,
    pagination: { limit: 20, offset: 0 },
  });
  const { data: targetCollabs, isLoading: isLoadingTarget } = useTargetCollaborationsList({
    search,
    pagination: { limit: 20, offset: 0 },
  });

  const options = useMemo(() => {
    const combined: { id: string; label: string }[] = [];

    if (openCollabs?.collaborations) {
      for (const collab of openCollabs.collaborations) {
        combined.push({
          id: `open:${collab.collaborationId}`,
          label: collab.productTitle,
        });
      }
    }

    if (targetCollabs?.collaborations) {
      for (const collab of targetCollabs.collaborations) {
        combined.push({
          id: `target:${collab.collaborationId}`,
          label: collab.name,
        });
      }
    }

    return combined;
  }, [openCollabs, targetCollabs]);

  const handleSetSearch = useCallback((value: string) => setSearch(value), []);

  return (
    <SearchableMultiSelect
      field={field}
      label="Collaboration"
      search={search}
      setSearch={handleSetSearch}
      options={options}
      isLoading={isLoadingOpen || isLoadingTarget}
      placeholder="Search collaborations..."
      emptyMessage="No collaborations found"
      loadingMessage="Loading..."
    />
  );
};

export const CollaborationFilter = memo(CollaborationFilterInner) as typeof CollaborationFilterInner;

const CampaignsEmptyState = ({
  icon,
  title,
  description,
  children,
}: {
  icon: FC<{ className?: string }>;
  title: string;
  description: string;
  children?: ReactNode;
}) => (
  <div className="flex flex-col items-center gap-4 px-4">
    <FeaturedIcon icon={icon} color="gray" size="sm" />
    <div className="flex w-full max-w-88 flex-col gap-1">
      <p className="text-md font-semibold text-primary">{title}</p>
      <p className="text-sm text-tertiary">{description}</p>
    </div>
    {children}
  </div>
);

const CampaignsNotConnectedState = () => {
  const { data: authData } = useTikTokAdsAuthUrl();
  const authUrl = authData?.authUrl;

  return (
    <CampaignsEmptyState
      icon={Link03}
      title="TikTok Ads not connected"
      description="Connect your TikTok Ads account to filter creators by the campaigns their videos run in."
    >
      <Button
        color="link-gray"
        size="sm"
        href={authUrl}
        target="_blank"
        rel="noopener noreferrer"
        isDisabled={!authUrl}
      >
        Connect TikTok Ads
      </Button>
    </CampaignsEmptyState>
  );
};

const NoCampaignsYetState = () => (
  <CampaignsEmptyState
    icon={Eye}
    title="No ad campaigns yet"
    description="Campaigns will appear here once your TikTok Ads account has run one featuring creator videos."
  />
);

const CampaignFilterInner = <TFieldValues extends FieldValues>({ field }: FilterFieldProps<TFieldValues>) => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  const { data, isLoading } = useCampaigns(debouncedSearch);
  const { data: connectionStatus, isLoading: isLoadingConnection } = useTikTokAdsConnectionStatus();
  const isConnected = connectionStatus?.connected === true;
  const isSearching = search !== "" || debouncedSearch !== "";

  const emptyState = !isConnected ? <CampaignsNotConnectedState /> : !isSearching ? <NoCampaignsYetState /> : undefined;

  const options = useMemo(() => {
    if (!data?.campaigns) return [];
    return data.campaigns.map((c) => ({ id: c.campaignId, label: c.campaignName }));
  }, [data]);

  const handleSetSearch = useCallback((value: string) => setSearch(value), []);

  return (
    <SearchableMultiSelect
      field={field}
      label="Ad Campaign"
      search={search}
      setSearch={handleSetSearch}
      options={options}
      isLoading={isLoading || isLoadingConnection}
      placeholder="Search campaigns..."
      emptyMessage="No campaigns found"
      emptyState={emptyState}
      loadingMessage="Loading..."
    />
  );
};

export const CampaignFilter = memo(CampaignFilterInner) as typeof CampaignFilterInner;
